<template>
  <div class="chart-wrapper widgetContainer" id="geoprocessingChartID"
    style="position: absolute; top: 100px; left: 350px; width: 550px; background: white; z-index: 5000;">
    <div class="chart-header" id="geoprocessingChartIDheader">
      <h5>{{ resolvedTitle }}</h5>
      <div class="buttons">
        <button v-if="hasPdfReport" class="header-btn" @click="downloadPdf" title="Descargar Reporte PDF">
          <font-awesome-icon icon="download" style="color: #d9534f;" />
        </button>
        <button class="header-btn" @click="toggleChartType" title="Cambiar tipo de gráfico">
          <font-awesome-icon :icon="chartType === 'pie' ? 'chart-bar' : 'chart-pie'" />
        </button>
        <button class="header-btn" @click="toggleVisible" title="Mostrar / Ocultar gráfico">
          <font-awesome-icon :icon="isHidden ? 'plus' : 'minus'" />
        </button>
        <button class="header-btn" @click="closeChart" title="Cerrar">
          <font-awesome-icon icon="times" />
        </button>
      </div>
    </div>
    <div class="chart-scroll-container">
      <div class="layer-selector-container">
         <select v-model="layerSelected" @change="onLayerChange" class="form-control">
          <option value="">[Seleccione Capa Temática]</option>
             <optgroup v-for="(category, index) in filteredCategories" :key="index" :label="category.name">
              <option v-for="layer in category.layers" :key="layer.id" :value="layer.id">
                {{ layer.name }}
              </option>
            </optgroup>
        </select>
        <div v-if="isLoadingLayers" style="font-size: 0.8em; color: #666; margin-top: 2px;">
           Cargando capas...
        </div>

        <!-- Pestañas de la herramienta -->
        <div class="tabs-container">
          <button class="btn btn-tab" :class="{ 'active': activeTab === 'table' }" @click="activeTab = 'table'" title="Mostrar tablas">
            <font-awesome-icon icon="table" />
          </button>
          <button class="btn btn-tab" :class="{ 'active': activeTab === 'chart' }" @click="activeTab = 'chart'" title="Gráfico de datos">
            <font-awesome-icon icon="chart-bar" />
          </button>
        </div>
      </div>

      <transition name="fade">
        <div v-show="!isHidden && (activeTab === 'chart' || !layerSelected)" class="chart-body" ref="chartBody"></div>
      </transition>

      <transition name="fade">
        <GeoprocessingTable 
          v-show="!isHidden && activeTab === 'table' && layerSelected"
          :layerSelected="layerSelected"
          :resultsTable="chartData.results_table"
          :rawResults="chartData.rawResults"
        />
      </transition>



      <transition name="fade">
        <div v-show="!isHidden && activeTab === 'chart' && hasPopulationData" class="chart-body" style="padding-top: 0;">
          <div v-for="(dataset, index) in populationDatasets" :key="index" style="margin-bottom: 20px;">
              <div class="chart-header" style="background: #004b7a; border-radius: 4px; padding: 5px 10px; margin-bottom: 5px;">
                  <h5 style="font-size: 0.9rem;">{{ dataset.title }}</h5>
              </div>
              <div ref="chartBodyPopulation"></div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { renderGeoprocessingChart } from "./graph.js";
import { draggableDiv } from "@/components/mixin/draggableDiv.js";
import { LayerService } from "@/services/layers/layerService";
import { mapService } from "@/services/map/mapService";
import GeoprocessingTable from "./GeoprocessingTable.vue";

export default {
  name: "GeoprocessingChart",
  mixins: [draggableDiv],
  components: {
    GeoprocessingTable
  },
  props: {
    chartData: { type: Object, required: true },
    title: { type: String, default: "" },
  },
  data() {
    return {
      chart: null,
      chart: null,
      populationCharts: [],
      chartType: "pie",
      activeTab: "chart",
      isHidden: false,
      layerSelected: "",
      filteredCategories: [],
      availableLayers: [], // Keeping for internal ref if needed, but UI uses filteredCategories
      isLoadingLayers: false,
      lastAddedLayerId: null
    };
  },
  computed: {
    resolvedTitle() {
      if (this.title) return this.title;
      // Safety check if chartData is empty
      if (!this.chartData) return "Resultado Geoproceso";
      
      const ignoredKeys = ["graphType", "availableLayers", "rawResults", "results_table", "filterGeometry", "population", "pdfReport"];
      const keys = Object.keys(this.chartData).filter(k => !ignoredKeys.includes(k));
      return keys.length > 0 ? keys[0] : "Resultado Geoproceso";
    },
    hasPopulationData() {
        return this.chartData && this.chartData.population;
    },
    hasPdfReport() {
        return this.chartData && this.chartData.pdfReport;
    },
    populationDatasets() {
        if (!this.hasPopulationData) return [];
        const popParams = this.chartData.population;
        // Exclude graphType and ensure we have arrays
        const keys = Object.keys(popParams).filter(k => k !== "graphType");
        return keys.map(k => ({
            title: k,
            data: {
                graphType: "Poblacion",
                [k]: popParams[k] 
            }
        }));
    }
  },
  mounted() {
    this.makeDraggable("geoprocessingChartID");
    // this.extractLayers(); // Deprecated in favor of store fetching
    this.populateLayers();
    this.renderChart();
  },
  methods: {
    downloadPdf() {
        if (!this.hasPdfReport) return;
        
        const pdfBase64 = this.chartData.pdfReport;
        const now = new Date();
        const day = String(now.getDate()).padStart(2, '0');
        const month = String(now.getMonth() + 1).padStart(2, '0');
        const year = now.getFullYear();
        const filename = `informe_${day}${month}${year}.pdf`;
        
        // Convert base64 to Blob
        const byteCharacters = atob(pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        
        // Create link and trigger download
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    },
    onLayerChange() {
      if (!this.layerSelected) return;
      
      // Remove previously added layer if exists
      if (this.lastAddedLayerId) {
          mapService.removeLayerFromMapByID(this.lastAddedLayerId);
          this.lastAddedLayerId = null;
      }

      // Search in filteredCategories
      let layer = null;
      for (const cat of this.filteredCategories) {
         const found = cat.layers.find(l => l.id == this.layerSelected);
         if (found) {
            layer = found;
            break;
         }
      }

      if (layer) {
        console.log("Adding layer from chart:", layer.name);
        
        let filterGeometry = null;
        if (this.chartData && this.chartData.filterGeometry) {
             filterGeometry = this.chartData.filterGeometry;
        }

        LayerService.addLayerToMap(layer, filterGeometry, false);
        this.lastAddedLayerId = layer.id;
      }
    },
    async populateLayers() {
       this.isLoadingLayers = true;
       // We need Radial (Point) and Lineal (Line)
       // LayerService.filterLayersByInfluence filters by ONE type.
       // We can fetch all and filter, or call it twice.
       // Calling it twice might duplicate category headers if we merge carelessly.
       
       try {
         const allLayers = this.$store.getters.getLayers || [];
         
         // Helper to filter manually to avoid multiple calls/merging issues
         // Logic adapted from LayerService.filterLayersByInfluence but for multiple types
         const typeLayerDictionary = { "esriGeometryPoint": "Radial", "esriGeometryPolygon": "Poligonal", "esriGeometryPolyline": "Lineal" };
         const acceptedTypes = ["Radial", "Lineal"];

         let categoriesFiltered = [];
         
         for (const category of allLayers) {
            let layersFiltered = [];
            for (const layer of category.layers) {
                // We need to fetch type if not present (LayerService does this)
                // Using LayerService.getLayerTypeArcgis directly
                const layerTypeArcgis = await LayerService.getLayerTypeArcgis(layer);
                
                if (layerTypeArcgis === "") continue;
                
                const typeLayer = typeLayerDictionary[layerTypeArcgis];
                if (acceptedTypes.includes(typeLayer)) {
                    layersFiltered.push(layer);
                }
            }
            if (layersFiltered.length > 0) {
                categoriesFiltered.push({
                    name: category.name,
                    layers: layersFiltered
                });
            }
         }
         this.filteredCategories = categoriesFiltered;
         
       } catch (error) {
          console.error("Error populating layers:", error);
       } finally {
          this.isLoadingLayers = false;
       }
    },
    extractLayers() {
       // Deprecated method, kept empty or remove if clean cleanup desired
    },
    toggleVisible() {
      this.isHidden = !this.isHidden;
    },
    toggleChartType() {
      this.chartType = this.chartType === "pie" ? "column" : "pie";
      this.renderChart();
    },
    closeChart() {
      if (this.lastAddedLayerId) {
          mapService.removeLayerFromMapByID(this.lastAddedLayerId);
          this.lastAddedLayerId = null;
      }
      this.$emit("close-chart");
    },
    renderChart() {
      if (this.chart) this.chart.destroy();
      this.chart = renderGeoprocessingChart(this.$refs.chartBody, this.chartData, this.chartType);
      
      if (this.hasPopulationData) {
          // Destroy old charts
          if (this.populationCharts.length) {
              this.populationCharts.forEach(c => c.destroy());
          }
          this.populationCharts = [];

          this.$nextTick(() => {
              const refs = this.$refs.chartBodyPopulation;
              if (Array.isArray(refs)) {
                  refs.forEach((el, index) => {
                      const dataset = this.populationDatasets[index];
                      if (dataset) {
                          const chartInstance = renderGeoprocessingChart(el, dataset.data, this.chartType);
                          if (chartInstance) this.populationCharts.push(chartInstance);
                      }
                  });
              }
          });
      }
    },
  },
  beforeDestroy() {
    if (this.lastAddedLayerId) {
        mapService.removeLayerFromMapByID(this.lastAddedLayerId);
    }
    if (this.chart) {
      this.chart.destroy();
    }
    if (this.populationCharts.length) {
        this.populationCharts.forEach(c => c.destroy());
    }
  },
  watch: {
    chartData: {
      deep: true,
      handler() {
        this.renderChart();
      },
    },
  },
};
</script>


<style scoped>
.chart-wrapper {
  width: 100%;
  background: transparent;
  border: none;
  box-shadow: none;
  position: relative;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.6em 0.8em;
  background: #006fb3;
  border-bottom: 1px solid #004b7a;
  border-radius: 6px 6px 0 0;
  color: white;
}

.chart-header h5 {
  margin: 0;
  font-weight: 700;
  font-size: 1rem;
  color: white;
  margin-right: 10px;
}

.buttons {
  display: flex;
  flex-wrap: nowrap;
  flex-shrink: 0;
  gap: 4px;
}

.header-btn {
  border: 1px solid #ddd;
  background: white;
  color: #006fb3;
  border-radius: 4px;
  transition: 0.2s ease;
  padding: 4px 8px;
  font-size: 0.85rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
}

.chart-body {
  padding: 1em;
  min-height: 400px;
  overflow: visible;
}

.header-btn:hover {
  background: #e8f4fb;
  border-color: #006fb3;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.layer-selector-container {
  padding: 0.5em 1em 0 1em; /* Align slightly with body padding */
}

.form-control {
  width: 100%;
  padding: 5px;
  border-radius: 4px;
  border: 1px solid #ccc;
  font-size: 0.9rem; /* Match typical font size */
}

.chart-scroll-container {
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
}

.tabs-container {
  display: flex;
  margin-top: 10px;
  gap: 10px;
  justify-content: center; /* Centrar botones */
}

.btn-tab {
  background: white;
  border: 1px solid #ddd;
  padding: 6px 20px; /* Un padding un poco más holgado */
  border-radius: 4px;
  color: #555;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 80px; /* Tamaño más razonable, en lugar de todo el ancho */
}

.btn-tab:hover {
  background: #f0f0f0;
  color: #333;
}

.btn-tab.active {
  background: #6c757d;
  color: white;
  border-color: #6c757d;
  box-shadow: inset 0 3px 5px rgba(0, 0, 0, 0.125);
}
</style>
